import { useCallback, useEffect, useMemo, useState } from "react";
import Select, { components, CSSObjectWithLabel } from "react-select";

import styles from "../Inputs/SelectControl.module.scss";

interface SelectProps {
    optionsData: ISelectControlOption[];
    maxMenuHeight: number;
    isMenuOpen?: boolean;
    onChangeCallback?: (value: string) => void;
    className?: string;
    value?: string;
    isEllipsis?: boolean;
    pageSize?: number;
    isDisabled?: boolean;
    useEmptyOption?: boolean;
    showPlaceHolderSearchIcon?: boolean;
    placeHolderText?: string;
}

interface ISelectControlOption {
    label: JSX.Element;
    value: string;
    filterLabel: string;
}

function DropdownIndicatorIcon(): JSX.Element {
    return (
        <svg height="15" width="15" viewBox="0 0 16 15" aria-hidden="true" focusable="false">
            <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z" />
        </svg>
    );
}

function SelectControl(props: SelectProps): JSX.Element {
    const getOptionsByPageSize = (options: ISelectControlOption[], start: number, end: number): ISelectControlOption[] => options?.slice(start, end);
    const [dataSource, setDataSource] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [selectedValue, setSelectedValue] = useState(props.value);

    const options = useMemo(
        () => {
            if (!props.useEmptyOption)
                return props.optionsData;

            return [
                { label: (<div className={styles.emptyOption} />), value: "", filterLabel: "" } as ISelectControlOption,
                ...props.optionsData
            ];
        },
        [props.optionsData, props.useEmptyOption]
    );

    function handleOnChange(selectedOption: ISelectControlOption): void {
        if (selectedOption) {
            props.onChangeCallback(selectedOption.value);
            setSelectedValue(selectedOption.value);
        }
    }

    function filterAllOptions(inputText: string, { action }: any): void {
        if (action === "input-change") {
            setSearchText(inputText);
            let filteredOptions = options.filter(option => option.filterLabel.toLowerCase().includes(inputText.toLowerCase()));
            let pageOptions = [];
            let filterOptions = [];
            if (filteredOptions.length > props.pageSize) {
                filterOptions = filteredOptions;
                pageOptions = getOptionsByPageSize(filteredOptions, 0, props.pageSize);
            }
            else {
                pageOptions = filteredOptions;
            }
            setFilteredData(filterOptions);
            setDataSource(pageOptions);
        }
    }

    const handleScrollBottom = useCallback(
        () => {
            if (options.length > props.pageSize) {
                const optionsLength = dataSource.length;
                const nextPageData = (searchText)
                    ? getOptionsByPageSize(filteredData, optionsLength, optionsLength + props.pageSize)
                    : getOptionsByPageSize(options, optionsLength, optionsLength + props.pageSize);
                setDataSource(dataSource.concat(nextPageData));
            }
        },
        [dataSource, filteredData, options, props.pageSize, searchText]
    );

    useEffect(
        () => setDataSource(getOptionsByPageSize(options, 0, props.pageSize)),
        [options, props.pageSize]
    );

    useEffect(() => {
        if (props.value !== selectedValue)
            setSelectedValue(props.value);
        return () => { };
    }, [props.value]);

    const customStyles = useMemo(
        () => ({
            control: (provided: CSSObjectWithLabel, state: { isFocused: boolean; }) => ({
                ...provided,
                borderColor: state.isFocused ? styles.borderColorFocused : styles.borderColorNormal,
                "&:hover": state.isFocused ? styles.borderColorFocused : styles.borderColorNormal,
                boxShadow: state.isFocused ? styles.borderBoxShadowFocused : "0"
            } as CSSObjectWithLabel),
            dropdownIndicator: (provided: CSSObjectWithLabel) => ({
                ...provided,
                padding: "0 1px 0 0"
            } as CSSObjectWithLabel),
            menu: (provided: CSSObjectWithLabel) => ({
                ...provided,
                margin: 0,
                padding: 0
            } as CSSObjectWithLabel),
            indicatorSeparator: (provided: CSSObjectWithLabel) => ({
                ...provided,
                display: "none"
            } as CSSObjectWithLabel),
            option: (provided: any) => ({
                ...provided,
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap"
            } as CSSObjectWithLabel)
        }),
        []
    );

    const DropdownIndicator = useCallback(
        props => (
            <components.DropdownIndicator {...props} className={styles.dropdownIconPadding}>
                <DropdownIndicatorIcon />
            </components.DropdownIndicator>
        ),
        []
    );

    function getSelectedOption(): ISelectControlOption | null {
        return selectedValue ? options?.firstOrDefault(option => option.value === selectedValue) : null;
    }

    return (
        <Select
            options={dataSource}
            maxMenuHeight={props.maxMenuHeight}
            styles={customStyles}
            components={{ DropdownIndicator }}
            menuIsOpen={props.isMenuOpen}
            onChange={option => handleOnChange(option)}
            placeholder={(
                <>
                    {props.showPlaceHolderSearchIcon &&
                        <i className={`fa fa-search ${styles.placeholderIconPosition}`} aria-hidden="true" />
                    }
                    {props.placeHolderText && <span className={styles.placeholderTextPadding}>{props.placeHolderText}</span>}
                </>
            )
            }
            value={getSelectedOption()}
            filterOption={() => true}
            onInputChange={filterAllOptions}
            inputValue={searchText}
            onMenuScrollToBottom={() => handleScrollBottom()}
            isDisabled={props.isDisabled}
        />
    );
}

export { ISelectControlOption, SelectControl };
